import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { DragSource, DropTarget } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import moment from 'moment';

import {
  isDirectory,
  isDocFSObject,
  isStudioFSObject,
} from './fsObjectUtils';

import {
  faPencilAlt,
  faCopy,
  faTrash,
  faDownload,
  faLink,
} from '@fortawesome/free-solid-svg-icons';

import DashItemMenu from 'components/DashItemMenu';
import ModifiedCell from 'components/table/ModifiedCell';
import DateCell from 'components/table/DateCell';

import {
  dragCollect,
  dropCollect,
  listItemDragSource,
  listItemTarget,
} from './fsObjectListItemDragDrop';

import { getFSObjectName, getFSObjectDesc } from './fsObjectUtils';

import { UPDATE_FOLDER, UPDATE_EVENT_DOC } from 'actions/Types';

import { BROADCAST_FORM } from 'constants/ModalTypes';

import FSObjectIcon from './FSObjectIcon';

import {
  IconWrapper,
  Name,
  Description,
  Date,
  StyledTD,
  SettingsTD,
  NameCellWrapper,
} from './FSObjectListItem.styled';

const FSObjectListItem = ({
  item,
  connectDragPreview,
  connectDragSource,
  connectDropTarget,
  draggingItemIds,
  selectedItemIds,
  canDrop,
  isOver,
  onClick,
  onSelectItem,
  backgroundColor = 'transparent',
  onArchiveClick,
  onUpdateFolderClick,
  isEventAdmin,
  onUpdateEventDocClick,
  onEditBroadcastClick,
  onCopyInviteLink,
  onDuplicateBroadcast,
}) => {
  useEffect(() => {
    if (connectDragPreview) {
      // Use empty image as a drag preview so browsers don't draw it
      // and we can draw whatever we want on the custom drag layer instead.
      connectDragPreview(getEmptyImage(), {
        // IE fallback: specify that we'd rather screenshot the node
        // when it already knows it's being dragged so we can hide it with CSS.
        captureDraggingState: true,
      });
    }
  }, [connectDragPreview]);

  const isDragging = draggingItemIds && draggingItemIds.indexOf(item._id) >= 0;
  const isDraggable = connectDragSource && connectDropTarget ? true : false;
  const isSelected = selectedItemIds.indexOf(item._id) >= 0;

  let background = backgroundColor;
  const color = '#333';

  if (canDrop) background = '#F7F9FE';
  if (canDrop && isOver) background = '#DCE1EA';

  const borderStyle = canDrop && isOver ? '2px solid #526589' : 'transparent';

  const trStyle = {
    background,
    color,
    borderTop: borderStyle,
    borderBottom: borderStyle,
    cursor: 'default',
    height: isDragging ? 0 : 1,
    opacity: isDragging ? 0 : 1,
  };

  const { parentId, reference } = item;

  let items = [];

  if (isDocFSObject(item)) {
    const { name, _id } = item.reference.object;

    items.push({
      onClick: onUpdateEventDocClick.bind(null, UPDATE_EVENT_DOC, {
        name,
        eventDocId: _id,
      }),
      icon: faPencilAlt,
      title: ' Edit file settings',
    });

    items.push({
      onClick: onArchiveClick.bind(null, item._id),
      icon: faTrash,
      title: ' Archive file',
    });

    items.push({
      onClick: onClick,
      icon: faDownload,
      title: ' Download file',
    });
  }

  if (isStudioFSObject(item)) {
    const { object } = reference;
    items = [
      ...items,
      {
        onClick: onEditBroadcastClick.bind(null, BROADCAST_FORM, object),
        icon: faPencilAlt,
        title: ' Edit studio settings',
      },
      {
        onClick: onDuplicateBroadcast.bind(null, object._id, parentId),
        icon: faCopy,
        title: ' Duplicate studio',
      },
      {
        onClick: onCopyInviteLink.bind(null, object._id),
        icon: faLink,
        title: ' Copy guest invite link',
      },
    ];

    items.push({
      onClick: onArchiveClick.bind(null, item._id),
      icon: faTrash,
      title: ' Archive studio',
    });
  }

  if (isDirectory(item)) {
    items.push({
      onClick: onArchiveClick.bind(null, item._id),
      icon: faTrash,
      title: ' Archive',
    });
    items.push({
      onClick: onUpdateFolderClick.bind(null, UPDATE_FOLDER, {
        existingDescription: item.description,
        parentPath: item.parentPath,
        existingName: item.name,
      }),
      icon: faPencilAlt,
      title: ' Edit folder settings',
    });
  }

  const settingsNode = isEventAdmin ? <DashItemMenu items={items} /> : null;

  const startOn = item.startOn;

  const content = (
    <tr
      style={trStyle}
      data-testid={'fsObject-' + getFSObjectName(item)}
      draggable={isDraggable}
      aria-grabbed={isDragging ? 'true' : 'false'}
      aria-dropeffect={canDrop ? 'move' : 'none'}
      onClick={onSelectItem}
      onDoubleClick={onClick}
    >
      <StyledTD isSelected={isSelected}>
        <IconWrapper onClick={onClick}>
          <FSObjectIcon item={item} />
        </IconWrapper>
      </StyledTD>
      <StyledTD isSelected={isSelected}>
        <NameCell
          name={getFSObjectName(item)}
          description={getFSObjectDesc(item)}
          date={item.startOn}
          onClick={onClick}
        />
      </StyledTD>
      <StyledTD className="d-none d-lg-table-cell" isSelected={isSelected}>
        {startOn && <DateCell dataTest="startDate" date={startOn} />}
      </StyledTD>
      <StyledTD className="d-none d-lg-table-cell" isSelected={isSelected}>
        <ModifiedCell dataTest="modifiedDate" item={item} />
      </StyledTD>
      <SettingsTD data-testid="fsObjectSettings" isSelected={isSelected}>
        {settingsNode}
      </SettingsTD>
    </tr>
  );

  if (!isDraggable) return content;

  return connectDragSource(connectDropTarget(content));
};

FSObjectListItem.propTypes = {
  item: PropTypes.object.isRequired,
  selectedItemIds: PropTypes.array.isRequired,
  onClick: PropTypes.func.isRequired,
  onArchiveClick: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  backgroundColor: PropTypes.string,
  onCopyInviteLink: PropTypes.func,
  onDuplicateBroadcast: PropTypes.func,
};

export const NameCell = ({ name, description, date, onClick }) => {
  return (
    <NameCellWrapper>
      <Name data-testid={'name-' + name} onClick={onClick}>
        {name}
      </Name>
      <Description>{description}</Description>
      {date && <Date>{moment(date).format('MMM d, Y')}</Date>}
    </NameCellWrapper>
  );
};

NameCell.propTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
  date: PropTypes.string,
  onClick: PropTypes.func,
};
const DraggableItem = DragSource(
  'FSObject',
  listItemDragSource,
  dragCollect,
)(FSObjectListItem);
const DroppableItem = DropTarget(
  'FSObject',
  listItemTarget,
  dropCollect,
)(DraggableItem);

export default DroppableItem;
